import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import Button from 'flarum/common/components/Button';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Link from 'flarum/common/components/Link';
import { brandLine, sponsorCard, signupBox } from '../sponsorHooks';
import { calculateTax } from '../taxRules';
import { taxExplainer } from '../taxExplainer';
const FREQUENCY_MULTIPLIERS = {
  yearly: 1,
  monthly: 12,
  biweekly: 26,
  weekly: 52,
  daily: 260
};
function formatCurrency(amount, currency) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'NGN',
    minimumFractionDigits: 2
  }).format(amount || 0);
}
const PERIOD_MULTIPLIERS = {
  yearly: 1,
  monthly: 1 / 12,
  biweekly: 1 / 26,
  weekly: 1 / 52,
  daily: 1 / 260
};
const PERIOD_LABELS = {
  yearly: 'Yearly',
  monthly: 'Monthly',
  biweekly: 'Biweekly',
  weekly: 'Weekly',
  daily: 'Daily'
};

/**
 * Where the income goes, as a doughnut drawn in SVG (no chart library): net pay and each deduction, with a key.
 */
function doughnut(r, breakdown) {
  const parts = [{
    label: 'Net pay',
    value: Math.max(0, r.netPay)
  }, ...breakdown].filter(p => p.value > 0);
  const total = parts.reduce((t, p) => t + p.value, 0) || 1;
  const R = 70,
    C = 2 * Math.PI * R;
  let offset = 0;
  const colour = i => i === 0 ? '#3fb18a' : CHART_COLORS[(i - 1) % CHART_COLORS.length];
  const rings = parts.map((p, i) => {
    const len = p.value / total * C;
    const el = m('circle', {
      cx: 90,
      cy: 90,
      r: R,
      fill: 'none',
      stroke: colour(i),
      'stroke-width': 28,
      'stroke-dasharray': len + ' ' + (C - len),
      'stroke-dashoffset': -offset,
      transform: 'rotate(-90 90 90)'
    });
    offset += len;
    return el;
  });
  return [m('svg', {
    viewBox: '0 0 180 180',
    width: 180,
    height: 180,
    role: 'img',
    'aria-label': parts.map(p => p.label + ' ' + Math.round(p.value / total * 100) + '%').join(', ')
  }, [rings, m('text', {
    x: 90,
    y: 86,
    'text-anchor': 'middle',
    'font-size': 12,
    fill: 'currentColor',
    opacity: 0.7
  }, 'Take-home'), m('text', {
    x: 90,
    y: 106,
    'text-anchor': 'middle',
    'font-size': 20,
    'font-weight': 700,
    fill: 'currentColor'
  }, Math.round(Math.max(0, r.netPay) / total * 100) + '%')]), m('ul.VouchaCalculator-chartKey', parts.map((p, i) => m('li', [m('i', {
    style: {
      background: colour(i)
    }
  }), p.label, m('b', Math.round(p.value / total * 100) + '%')])))];
}
const CHART_COLORS = ['#e0714a', '#4a90c0', '#f0b429', '#8e5cd9', '#3fb18a'];
const DEFAULT_SALARIES = {
  NG: 5000000,
  GB: 50000,
  CA: 80000,
  US: 70000
};
export default class TaxCalculatorPage extends Component {
  oninit(vnode) {
    super.oninit(vnode);
    this.configLoading = true;
    this.config = null; // populated from API: { NG: {...}, US: {...}, CA: {...}, GB: {...} }

    this.country = 'NG';
    this.region = 'FEDERAL';
    this.salary = DEFAULT_SALARIES.NG;
    this.bonus = 0;
    this.commission = 0;
    this.frequency = 'yearly';
    // Nigeria: pension (8% of salary) and rent relief (20% of yearly rent, capped)
    this.ngPension = true;
    this.ngRent = '';
    this.results = null;
    this.error = null;
    this.saving = false;
    this.saveMessage = null;
    this.activePeriod = 'yearly';
    this.exchangeRates = {
      USD: 1580,
      CAD: 1130,
      GBP: 1950,
      NGN: 1
    };
    this.loadExchangeRates();
    this.loadConfig();
    this.history = null;
    this.loadingHistory = false;
  }
  async loadConfig() {
    try {
      const response = await app.request({
        method: 'GET',
        url: app.forum.attribute('apiUrl') + '/voucha-calculators/tax/config'
      });
      if (response.success) {
        this.config = response.countries;
        this.region = this.regionsFor(this.country)[0]?.code || 'FEDERAL';
        if (app.session.user) this.loadHistory();
      } else {
        this.error = 'Could not load tax rate data.';
      }
    } catch (e) {
      this.error = 'Could not load tax rate data. Please try again later.';
    }
    this.configLoading = false;
    m.redraw();
  }
  async loadExchangeRates() {
    try {
      const response = await fetch('https://api.exchangerate-api.com/v4/latest/NGN');
      if (!response.ok) throw new Error('rate fetch failed');
      const data = await response.json();
      this.exchangeRates = {
        USD: Math.round(1 / data.rates.USD),
        CAD: Math.round(1 / data.rates.CAD),
        GBP: Math.round(1 / data.rates.GBP),
        NGN: 1
      };
      m.redraw();
    } catch (e) {
      // fallback rates already set
    }
  }
  regionsFor(country) {
    if (!this.config || !this.config[country]) return [];
    return this.config[country].regions || [];
  }
  onCountryChange(country) {
    this.country = country;
    const regions = this.regionsFor(country);
    this.region = regions[0]?.code || 'FEDERAL';
    this.salary = DEFAULT_SALARIES[country] || 5000000;
    this.results = null;
  }
  reset() {
    this.country = 'NG';
    this.region = this.regionsFor('NG')[0]?.code || 'FEDERAL';
    this.salary = DEFAULT_SALARIES.NG;
    this.bonus = 0;
    this.commission = 0;
    this.frequency = 'yearly';
    this.results = null;
    this.error = null;
    this.saveMessage = null;
    this.activePeriod = 'yearly';
  }
  calculate() {
    if (!this.config || !this.config[this.country]) return;
    const salary = parseFloat(this.salary) || 0;
    const bonus = parseFloat(this.bonus) || 0;
    const commission = parseFloat(this.commission) || 0;
    if (salary <= 0) {
      this.error = 'Please enter a valid salary amount.';
      return;
    }
    this.error = null;
    const yearlyIncome = salary * (FREQUENCY_MULTIPLIERS[this.frequency] || 1);
    const totalIncome = yearlyIncome + bonus + commission;
    const countryConfig = this.config[this.country];
    const tax = calculateTax(countryConfig, totalIncome, yearlyIncome, this.region, {
      pension: this.ngPension,
      rent: parseFloat(this.ngRent) || 0
    });
    const totalDeductions = tax.total;
    const netPay = totalIncome - totalDeductions;
    const effectiveTaxRate = totalIncome > 0 ? totalDeductions / totalIncome * 100 : 0;
    this.results = {
      country: this.country,
      region: this.region,
      currency: countryConfig.currency,
      totalIncome,
      lines: tax.lines,
      taxable: tax.taxable,
      details: tax.details,
      totalDeductions,
      netPay,
      salary,
      bonus,
      commission,
      effectiveTaxRate
    };

    // Auto-select the results tab matching what the user entered, same as
    // the original standalone calculator did.
    this.activePeriod = FREQUENCY_MULTIPLIERS[this.frequency] ? this.frequency : 'yearly';

    // Auto-save immediately on calculate (logged-in users) — the explicit
    // Save button remains for a manual re-save/confirmation, but the user
    // shouldn't have to click it for the calculation to actually be recorded.
    if (app.session.user) {
      this.save();
    }
  }
  async save() {
    if (!this.results || !app.session.user) return;
    this.saving = true;
    this.saveMessage = null;
    m.redraw();
    let totalIncomeNGN = this.results.totalIncome;
    let netPayNGN = this.results.netPay;
    if (this.results.currency !== 'NGN') {
      const rate = this.exchangeRates[this.results.currency] || 1;
      totalIncomeNGN = this.results.totalIncome * rate;
      netPayNGN = this.results.netPay * rate;
    }
    try {
      const response = await app.request({
        method: 'POST',
        url: app.forum.attribute('apiUrl') + '/voucha-calculators/tax/save',
        body: {
          country: this.results.country,
          region: this.results.region,
          pay_frequency: this.frequency,
          salary_amount: this.results.salary,
          bonus: this.results.bonus,
          commission: this.results.commission,
          total_income_ngn: totalIncomeNGN,
          net_pay_ngn: netPayNGN
        }
      });
      if (response.success) {
        this.saveMessage = {
          type: 'success',
          text: 'Calculation saved to your account.'
        };
        this.history = null;
        this.loadHistory();
      } else {
        this.saveMessage = {
          type: 'error',
          text: response.message || 'Could not save.'
        };
      }
    } catch (e) {
      this.saveMessage = {
        type: 'error',
        text: 'Could not save. Please try again.'
      };
    }
    this.saving = false;
    m.redraw();
  }
  async loadHistory() {
    if (!app.session.user) return;
    this.loadingHistory = true;
    m.redraw();
    try {
      const response = await app.request({
        method: 'GET',
        url: app.forum.attribute('apiUrl') + '/voucha-calculators/tax/history'
      });
      this.history = response.success ? response.data : [];
    } catch (e) {
      this.history = [];
    }
    this.loadingHistory = false;
    m.redraw();
  }
  async clearHistory() {
    if (!window.confirm('Permanently delete all your saved tax calculations? This cannot be undone.')) {
      return;
    }
    this.loadingHistory = true;
    m.redraw();
    try {
      await app.request({
        method: 'DELETE',
        url: app.forum.attribute('apiUrl') + '/voucha-calculators/tax/history'
      });
      this.history = [];
    } catch (e) {
      // leave existing history displayed if the delete failed
    }
    this.loadingHistory = false;
    m.redraw();
  }
  view() {
    if (this.configLoading) {
      return m("div", {
        className: "VouchaCalculator container"
      }, LoadingIndicator.component());
    }
    if (!this.config) {
      return m("div", {
        className: "VouchaCalculator container"
      }, m("div", {
        className: "VouchaCalculator-error"
      }, this.error || 'Could not load calculator.'));
    }
    const regions = this.regionsFor(this.country);
    return m("div", {
      className: "VouchaCalculator container"
    }, m("div", {
      className: "VouchaCalculator-header"
    }, m("h1", null, "Income tax calculator: Nigeria, the UK, the US and Canada"), m("p", {
      className: "helpText"
    }, "Work out your take-home pay after income tax and contributions for the 2026 tax year (2026/27 in the UK). How each country's tax is worked out, with this year's bands, is under the calculator.")), m("div", {
      className: "VouchaCalculator-form"
    }, m("div", {
      className: "Form-group"
    }, m("label", null, "Country"), m("select", {
      value: this.country,
      onchange: e => this.onCountryChange(e.target.value)
    }, Object.keys(this.config).map(code => m("option", {
      value: code
    }, this.config[code].name)))), m("div", {
      className: "Form-group"
    }, m("label", null, "Region"), m("select", {
      value: this.region,
      onchange: e => this.region = e.target.value
    }, regions.map(r => m("option", {
      value: r.code
    }, r.name)))), m("div", {
      className: "Form-group"
    }, m("label", null, "Salary"), m("input", {
      type: "number",
      value: this.salary,
      oninput: e => this.salary = e.target.value
    })), m("div", {
      className: "Form-group"
    }, m("label", null, "Pay Frequency"), m("select", {
      value: this.frequency,
      onchange: e => this.frequency = e.target.value
    }, m("option", {
      value: "yearly"
    }, "Yearly"), m("option", {
      value: "monthly"
    }, "Monthly"), m("option", {
      value: "biweekly"
    }, "Biweekly"), m("option", {
      value: "weekly"
    }, "Weekly"), m("option", {
      value: "daily"
    }, "Daily"))), m("div", {
      className: "Form-group"
    }, m("label", null, "Bonus (yearly)"), m("input", {
      type: "number",
      value: this.bonus,
      oninput: e => this.bonus = e.target.value
    })), m("div", {
      className: "Form-group"
    }, m("label", null, "Commission (yearly)"), m("input", {
      type: "number",
      value: this.commission,
      oninput: e => this.commission = e.target.value
    })), this.country === 'NG' ? m("div", {
      className: "Form-group"
    }, m("label", null, "Rent you pay (yearly, optional)"), m("input", {
      type: "number",
      placeholder: "e.g. 1,200,000",
      value: this.ngRent,
      oninput: e => this.ngRent = e.target.value
    }), m("small", {
      className: "helpText"
    }, "20% of it comes off your taxable income, up to \u20A6500,000 a year (rent relief).")) : null, this.country === 'NG' ? m("div", {
      className: "Form-group VouchaCalculator-check"
    }, m("label", null, m("input", {
      type: "checkbox",
      checked: this.ngPension,
      onchange: e => this.ngPension = e.target.checked
    }), " I pay into a pension (8% of salary)")) : null, this.error && m("div", {
      className: "VouchaCalculator-error"
    }, this.error), m("div", {
      className: "VouchaCalculator-formActions"
    }, Button.component({
      className: 'Button Button--primary',
      onclick: () => this.calculate()
    }, 'Calculate'), Button.component({
      className: 'Button',
      onclick: () => this.reset()
    }, 'Reset'))), this.results && sponsorCard('tax', this.results.country), this.results && this.renderResults(), taxExplainer(this.config, this.country), m("div", {
      className: "VouchaCalculator-relatedTools"
    }, m("h2", null, "Related tools"), m(Link, {
      href: "/salary-converter"
    }, "Salary Converter"), m(Link, {
      href: "/business-tax"
    }, "Business Tax Calculator")), signupBox('calculator-tax'), app.session.user && this.renderHistorySection());
  }
  renderResults() {
    const r = this.results;
    const countryName = this.config[r.country].name;
    const multiplier = PERIOD_MULTIPLIERS[this.activePeriod];
    const breakdown = r.lines;
    return m("div", {
      className: "VouchaCalculator-results"
    }, m("h2", null, "Results (", countryName, ")"), brandLine('tax', r.country, countryName + ' tax results'), m("div", {
      className: "VouchaCalculator-periodTabs"
    }, Object.keys(PERIOD_LABELS).map(period => m("button", {
      className: `VouchaCalculator-periodTab${period === this.activePeriod ? ' is-active' : ''}`,
      onclick: () => this.activePeriod = period
    }, PERIOD_LABELS[period]))), m("div", {
      className: "VouchaCalculator-resultRow"
    }, m("span", null, "Total Income"), m("span", null, formatCurrency(r.totalIncome * multiplier, r.currency))), m("div", {
      className: "VouchaCalculator-resultRow"
    }, m("span", null, "Total Deductions"), m("span", null, formatCurrency(r.totalDeductions * multiplier, r.currency))), breakdown.map(d => m("div", {
      className: "VouchaCalculator-resultRow VouchaCalculator-resultRow--sub"
    }, m("span", null, d.label), m("span", null, formatCurrency(d.value * multiplier, r.currency)))), m("div", {
      className: "VouchaCalculator-resultRow VouchaCalculator-resultRow--net"
    }, m("span", null, "Net Pay"), m("span", null, formatCurrency(r.netPay * multiplier, r.currency))), m("div", {
      className: "VouchaCalculator-resultRow"
    }, m("span", null, "Effective Tax Rate"), m("span", null, r.effectiveTaxRate.toFixed(2), "%")), m("div", {
      className: "VouchaCalculator-chartWrap"
    }, doughnut(r, breakdown)), r.country === 'NG' && this.renderTaxBreakdown(r), m("div", {
      className: "VouchaCalculator-summary"
    }, m("h3", null, "Summary"), m("div", {
      className: "VouchaCalculator-summaryGrid"
    }, m("div", {
      className: "VouchaCalculator-summaryCard"
    }, m("span", null, "Monthly Salary"), m("strong", null, formatCurrency(r.totalIncome / 12, r.currency))), m("div", {
      className: "VouchaCalculator-summaryCard"
    }, m("span", null, "Monthly deductions"), m("strong", null, formatCurrency(r.totalDeductions / 12, r.currency))), m("div", {
      className: "VouchaCalculator-summaryCard"
    }, m("span", null, "Effective Tax Rate"), m("strong", null, r.effectiveTaxRate.toFixed(1), "%")), m("div", {
      className: "VouchaCalculator-summaryCard"
    }, m("span", null, "Monthly Net Pay"), m("strong", null, formatCurrency(r.netPay / 12, r.currency))))), app.session.user ? m("div", {
      className: "VouchaCalculator-saveArea"
    }, Button.component({
      className: 'Button',
      onclick: () => this.save(),
      loading: this.saving
    }, 'Save to my account'), this.saveMessage && m("div", {
      className: `VouchaCalculator-saveMessage VouchaCalculator-saveMessage--${this.saveMessage.type}`
    }, this.saveMessage.text)) : m("div", {
      className: "VouchaCalculator-loginPrompt"
    }, m("span", null, "Log in to save this calculation to your account."), Button.component({
      className: 'Button Button--primary',
      onclick: () => {
        // Rather than reconstruct Flarum's login modal ourselves
        // (fragile — its exact module registry key varies by core
        // version), just trigger the real header Log In button,
        // which is already correctly wired up by Flarum itself.
        const headerLoginBtn = document.querySelector('.item-logIn .Button');
        if (headerLoginBtn) {
          headerLoginBtn.click();
        } else {
          // Fallback if the header button isn't in the DOM for some
          // reason (e.g. custom header layout hides it) — send them
          // to the forum root where it's guaranteed to be present.
          m.route.set('/');
        }
      }
    }, 'Log In')));
  }

  /** Nigeria: from gross income to taxable income, then the tax in each band */
  renderTaxBreakdown(r) {
    const brackets = this.config.NG.brackets;
    const taxable = r.taxable;
    const paye = (r.lines.find(l => l.key === 'payeTax') || {
      value: 0
    }).value;
    let remaining = taxable;
    const rows = [];
    for (const bracket of brackets) {
      const inBand = Math.min(Math.max(0, remaining), bracket.max - bracket.min);
      const rangeLabel = bracket.min === 0 ? `First ₦${bracket.max.toLocaleString('en-NG')}` : bracket.max >= 999999999 ? `Above ₦${bracket.min.toLocaleString('en-NG')}` : `Next ₦${(bracket.max - bracket.min).toLocaleString('en-NG')}`;
      rows.push({
        range: `${rangeLabel} @ ${(bracket.rate * 100).toFixed(0)}%`,
        amount: inBand,
        tax: inBand * bracket.rate
      });
      remaining -= inBand;
      if (taxable <= bracket.max) break;
    }
    const d = r.details || {};
    return m("div", {
      className: "VouchaCalculator-breakdown"
    }, m("h3", null, "Tax Breakdown"), m("table", {
      className: "VouchaCalculator-breakdownTable"
    }, m("tbody", null, m("tr", null, m("td", null, "Total income"), m("td", null, formatCurrency(r.totalIncome, 'NGN')), m("td", null)), d.pension ? m("tr", null, m("td", null, "Less pension (8% of salary)"), m("td", null, "\u2212", formatCurrency(d.pension, 'NGN')), m("td", null)) : null, d.rentRelief ? m("tr", null, m("td", null, "Less rent relief"), m("td", null, "\u2212", formatCurrency(d.rentRelief, 'NGN')), m("td", null)) : null, m("tr", {
      className: "VouchaCalculator-breakdownTotal"
    }, m("td", null, "Taxable income"), m("td", null, formatCurrency(taxable, 'NGN')), m("td", null)))), m("table", {
      className: "VouchaCalculator-breakdownTable"
    }, m("thead", null, m("tr", null, m("th", null, "Tax band"), m("th", null, "Taxable income in band"), m("th", null, "Tax due"))), m("tbody", null, rows.map(row => m("tr", null, m("td", null, row.range), m("td", null, formatCurrency(row.amount, 'NGN')), m("td", null, formatCurrency(row.tax, 'NGN')))), m("tr", {
      className: "VouchaCalculator-breakdownTotal"
    }, m("td", null, "Total"), m("td", null, formatCurrency(taxable, 'NGN')), m("td", null, formatCurrency(paye, 'NGN'))))));
  }
  renderHistorySection() {
    return m("div", {
      className: "VouchaCalculator-history"
    }, m("div", {
      className: "VouchaCalculator-historyHeader"
    }, m("h2", null, "My Saved Calculations"), m("div", {
      className: "VouchaCalculator-historyActions"
    }, Button.component({
      className: 'Button Button--link',
      onclick: () => this.loadHistory()
    }, 'Refresh'), this.history && this.history.length > 0 && Button.component({
      className: 'Button Button--link VouchaCalculator-clearLink',
      onclick: () => this.clearHistory()
    }, 'Clear'))), this.loadingHistory && LoadingIndicator.component(), this.history && this.history.length === 0 && m("p", {
      className: "helpText"
    }, "No saved calculations yet."), this.history && this.history.length > 0 && m("table", {
      className: "VouchaCalculator-historyTable"
    }, m("thead", null, m("tr", null, m("th", null, "Country"), m("th", null, "Region"), m("th", null, "Total Income (\u20A6)"), m("th", null, "Bonus (\u20A6)"), m("th", null, "Net Pay (\u20A6)"), m("th", null, "Effective Tax Rate"))), m("tbody", null, this.history.map(row => {
      const totalIncome = parseFloat(row.total_income_ngn) || 0;
      const netPay = parseFloat(row.net_pay_ngn) || 0;
      const effectiveRate = totalIncome > 0 ? (totalIncome - netPay) / totalIncome * 100 : 0;
      return m("tr", null, m("td", null, row.country), m("td", null, row.region || '—'), m("td", null, formatCurrency(totalIncome, 'NGN')), m("td", null, formatCurrency(parseFloat(row.bonus) || 0, 'NGN')), m("td", null, formatCurrency(netPay, 'NGN')), m("td", null, effectiveRate.toFixed(2), "%"));
    }))));
  }
}
flarum.reg.add('voucha-calculators', 'forum/components/TaxCalculatorPage', TaxCalculatorPage);